import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { clearTokens, getOperationHistory, formatApiError, getCurrentUserProfile, getProfilePictureUrl } from '../services/api';
import AdminSidebar from './AdminSidebar';
import AdminTopBar from './AdminTopBar';
import NotificationSidebar from './NotificationSidebar';
import OperationsPage from './OperationsPage';
import ManagerDashboardView from './ManagerDashboardView';
import ManagerTeamsPage from './ManagerTeamsPage';
import BadgeConfigurationPage from './BadgeConfigurationPage';
import RewardManagementPage from './RewardManagementPage';
import GlobalPerformanceTrackingPage from './GlobalPerformanceTrackingPage';
import BadgeDisputeManagementPage from './BadgeDisputeManagementPage';
import ImputationsTablePage from './ImputationsTablePage';
import TeamsPlanningPage from './TeamsPlanningPage';
import {
  TeamsPlanningCentreFilterRow,
  TeamsPlanningExportButton,
  TeamsPlanningFilters,
  TeamsPlanningMemberFilterRow,
  TeamsPlanningPeopleFiltersPanel,
  TeamsPlanningPeopleFiltersToggle,
  TeamsPlanningTeamFilterRow,
  useTeamsPlanningFilters,
} from './teamsPlanning/FilterBar';
import TeamsPlanningSeeDetailsButton from './teamsPlanning/TeamsPlanningSeeDetailsButton';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('nav');
  const { t: tManager } = useTranslation('manager');
  const { t: tCommon } = useTranslation('common');
  const { t: tAdmin } = useTranslation('admin');
  const teamsPlanningFilters = useTeamsPlanningFilters();
  const getInitialManagerMenuItem = () => {
    const defaultMenuItem = 'dashboard';
    const allowedMenuItems = [
      'dashboard',
      'imputations',
      'my-teams',
      'teams-planning',
      'operations',
      'badge-configuration',
      'reward-management',
      'performance-tracking',
      'operation-logs',
      'badge-disputes',
    ];
    try {
      const savedMenuItem = localStorage.getItem('managerActiveMenuItem');
      return allowedMenuItems.includes(savedMenuItem) ? savedMenuItem : defaultMenuItem;
    } catch (error) {
      return defaultMenuItem;
    }
  };
  const [activeMenuItem, setActiveMenuItem] = useState(getInitialManagerMenuItem);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  
  // Current user data state
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  // Operation logs state
  const [operationLogs, setOperationLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [selectedLogItem, setSelectedLogItem] = useState(null);
  const [showLogDetailsModal, setShowLogDetailsModal] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [logsFilters, setLogsFilters] = useState({
    date_from: '',
    date_to: '',
    action_type: '',
    user: '',
    search: '',
  });
  
  // Users list for "who" filter
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // User combobox state
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const handleMenuItemClick = (itemId) => {
    setActiveMenuItem(itemId);
  };

  const handleSignOut = () => {
    clearTokens();
    navigate('/login', { replace: true });
  };

  const handleSearch = (searchValue) => {
    // Handle search logic here
    console.log('Searching for:', searchValue);
  };

  const handleMenuClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleCloseMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleHelpClick = () => {
    // Handle help click
    console.log('Help clicked');
  };

  const handleSettingsClick = () => {
    // Handle settings click
    console.log('Settings clicked');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  // Fetch current user data on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoadingUser(true);
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        // Set default values if fetch fails
        setCurrentUser({
          first_name: '',
          last_name: '',
          email: '',
          role: 'MANAGER'
        });
      } finally {
        setLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('managerActiveMenuItem', activeMenuItem);
    } catch (error) {
      // Ignore storage write failures (private mode/quota)
    }
  }, [activeMenuItem]);

  // Helper functions for formatting
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    return date.toLocaleString(getDateLocale(), {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatActionType = (actionType) => {
    const types = {
      'CREATE': { label: tAdmin('logs.created'), color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70', icon: 'add_circle' },
      'UPDATE': { label: tAdmin('logs.updated'), color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70', icon: 'edit' },
      'ACTIVATE': { label: tAdmin('logs.activated'), color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70', icon: 'check_circle' },
      'DEACTIVATE': { label: tAdmin('logs.deactivated'), color: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70', icon: 'cancel' },
    };
    return types[actionType] || { label: actionType, color: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80', icon: 'info' };
  };

  const formatUserName = (logItem) => {
    if (!logItem) return tAdmin('logs.system');
    // Handle direct user object
    if (logItem.modified_by && typeof logItem.modified_by === 'object') {
      const user = logItem.modified_by;
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim();
      }
      return user.email || tAdmin('logs.unknown');
    }
    // Handle when user fields are at root level (from serializer)
    if (logItem.modified_by_first_name || logItem.modified_by_last_name) {
      return `${logItem.modified_by_first_name || ''} ${logItem.modified_by_last_name || ''}`.trim();
    }
    return logItem.modified_by_email || tAdmin('logs.system');
  };

  const getChangedFields = (logItem) => {
    const fields = [];
    if (logItem.old_code !== logItem.new_code) fields.push({ name: tAdmin('logs.code'), old: logItem.old_code, new: logItem.new_code });
    if (logItem.old_name !== logItem.new_name) fields.push({ name: tAdmin('logs.name'), old: logItem.old_name, new: logItem.new_name });
    if (logItem.old_domain !== logItem.new_domain) fields.push({ name: tAdmin('logs.domain'), old: logItem.old_domain, new: logItem.new_domain });
    if (logItem.old_description !== logItem.new_description) fields.push({ name: tAdmin('logs.description'), old: logItem.old_description, new: logItem.new_description });
    if (logItem.old_temps_standard !== logItem.new_temps_standard) fields.push({ name: tAdmin('logs.standardTime'), old: logItem.old_temps_standard, new: logItem.new_temps_standard });
    if (logItem.old_is_active !== logItem.new_is_active) fields.push({ name: tAdmin('logs.activeStatus'), old: logItem.old_is_active, new: logItem.new_is_active });
    return fields;
  };

  const formatFieldValue = (value, fieldName) => {
    if (value === null || value === undefined || value === '') return '—';
    if (fieldName === tAdmin('logs.activeStatus')) {
      return value === true || value === 'true' ? tCommon('active') : tCommon('inactive');
    }
    if (fieldName === tAdmin('logs.standardTime')) {
      // API returns HH:MM format, display as-is
      return value || '—';
    }
    return value;
  };

  // Fetch users list for "who" filter (only users who have operation history)
  const fetchUsersList = async () => {
    setLoadingUsers(true);
    try {
      // Fetch operation history to get all unique users who have made changes
      const response = await getOperationHistory({}, 1, 1000); // Large page size to get all
      
      const logsArray = Array.isArray(response) 
        ? response 
        : (response && response.results && Array.isArray(response.results)) 
          ? response.results 
          : [];
      
      // Extract unique users from operation history
      const uniqueUsersMap = new Map();
      logsArray.forEach(log => {
        if (log.modified_by) {
          // Handle both object and ID formats
          const userId = typeof log.modified_by === 'object' ? log.modified_by.id : log.modified_by;
          if (userId && !uniqueUsersMap.has(userId)) {
            // Use user object if available, otherwise construct from log fields
            const user = typeof log.modified_by === 'object' 
              ? log.modified_by
              : {
                  id: userId,
                  first_name: log.modified_by_first_name || '',
                  last_name: log.modified_by_last_name || '',
                  email: log.modified_by_email || ''
                };
            uniqueUsersMap.set(userId, user);
          }
        }
      });
      
      // Convert map to array and sort by name
      const usersArray = Array.from(uniqueUsersMap.values()).sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setUsersList(usersArray);
    } catch (error) {
      console.error('Error fetching users from operation history:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch operation logs
  const fetchOperationLogs = async () => {
    setLoadingLogs(true);
    setLogsError(null);
    try {
      const filters = {};
      if (logsFilters.date_from) filters.date_from = logsFilters.date_from;
      if (logsFilters.date_to) filters.date_to = logsFilters.date_to;
      if (logsFilters.action_type) filters.action_type = logsFilters.action_type;
      if (logsFilters.user) filters.user = logsFilters.user;
      if (logsFilters.search) filters.operation = logsFilters.search;

      const response = await getOperationHistory(filters, logsPage, logsPageSize);
      
      const logsArray = Array.isArray(response) 
        ? response 
        : (response && response.results && Array.isArray(response.results)) 
          ? response.results 
          : [];
      
      setOperationLogs(logsArray);
      
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        setLogsTotalCount(response.count || 0);
      } else {
        setLogsTotalCount(logsArray.length);
      }
    } catch (error) {
      const formattedError = formatApiError(error);
      setLogsError(formattedError.generalError || tAdmin('logs.loadFailed'));
      console.error('Error fetching operation logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Fetch users list when operation-logs menu is active
  useEffect(() => {
    if (activeMenuItem === 'operation-logs') {
      fetchUsersList();
    }
  }, [activeMenuItem]);
  
  // Sync userSearchQuery when user filter changes externally
  useEffect(() => {
    if (!logsFilters.user && userSearchQuery) {
      setUserSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsFilters.user]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setLogsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsFilters.date_from, logsFilters.date_to, logsFilters.action_type, logsFilters.user, logsFilters.search]);

  // Fetch logs when filters, page, or page size changes (only for operation-logs menu)
  useEffect(() => {
    if (activeMenuItem === 'operation-logs') {
      fetchOperationLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsPage, logsPageSize, logsFilters, activeMenuItem]);

  // Handle modal open/close
  const handleOpenLogDetails = (logItem) => {
    setSelectedLogItem(logItem);
    setShowLogDetailsModal(true);
  };

  const handleCloseLogDetails = () => {
    setShowLogDetailsModal(false);
    setSelectedLogItem(null);
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showLogDetailsModal) {
        handleCloseLogDetails();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLogDetailsModal]);

  // Render Operation Logs Page
  const renderOperationLogsPage = () => {
    const startIndex = (logsPage - 1) * logsPageSize + 1;
    const endIndex = Math.min(logsPage * logsPageSize, logsTotalCount);
    const totalPages = Math.ceil(logsTotalCount / logsPageSize);

    const handlePageChange = (newPage) => {
      setLogsPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleClearFilters = () => {
      setLogsFilters({
        date_from: '',
        date_to: '',
        action_type: '',
        user: '',
        search: '',
      });
      setUserSearchQuery('');
      setLogsPage(1);
    };

    const getActionTypeBadgeClass = (actionType) => {
      const classes = {
        'CREATE': 'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70',
        'UPDATE': 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70',
        'ACTIVATE': 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70',
        'DEACTIVATE': 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70',
      };
      return classes[actionType] || 'bg-slate-50 text-slate-700 border-slate-100 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80';
    };

    const getActionTypeIcon = (actionType) => {
      const icons = {
        'CREATE': 'add_circle',
        'UPDATE': 'edit',
        'ACTIVATE': 'check_circle',
        'DEACTIVATE': 'cancel',
      };
      return icons[actionType] || 'info';
    };
    
    // Get selected user display name
    const getSelectedUserName = () => {
      if (!logsFilters.user) return '';
      const selectedUser = usersList.find(u => u.id.toString() === logsFilters.user.toString());
      if (selectedUser) {
        return `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`;
      }
      return '';
    };
    
    // Filter users based on search query
    const filteredUsers = usersList.filter(user => {
      if (!userSearchQuery) return true;
      const searchLower = userSearchQuery.toLowerCase();
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

    return (
      <div className="flex flex-col gap-6">
        {/* Page Heading */}
        <div className="flex flex-wrap justify-between items-end gap-4 px-1">
          <div className="flex min-w-72 flex-col gap-2">
            <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
              {tAdmin('logs.systemTitle')}
            </h1>
            <p className="text-slate-500 text-base font-normal leading-normal">
              {tAdmin('logs.subtitle')}
            </p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col min-w-64 flex-1">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider pb-2">
                {tAdmin('logs.searchOperations')}
              </p>
              <div className="relative">
                <input
                  className="form-input w-full rounded-lg text-slate-900 focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-slate-200 bg-slate-50 h-10 placeholder:text-slate-400 pl-10 pr-4 text-sm"
                  placeholder={tAdmin('logs.searchPlaceholder')}
                  value={logsFilters.search}
                  onChange={(e) => setLogsFilters({...logsFilters, search: e.target.value})}
                />
                <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
              </div>
            </label>
            <div className="flex gap-3 flex-wrap items-end">
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Date From</p>
                <input 
                  className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary" 
                  type="date"
                  value={logsFilters.date_from}
                  onChange={(e) => setLogsFilters({...logsFilters, date_from: e.target.value})}
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Date To</p>
                <input 
                  className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary" 
                  type="date"
                  value={logsFilters.date_to}
                  onChange={(e) => setLogsFilters({...logsFilters, date_to: e.target.value})}
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Action Type</p>
                <div className="relative">
                  <select
                    value={logsFilters.action_type}
                    onChange={(e) => setLogsFilters({...logsFilters, action_type: e.target.value})}
                    className="h-10 appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 min-w-[160px] text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                  >
                    <option value="">{tAdmin('logs.allActions')}</option>
                    <option value="CREATE">{tAdmin('logs.created')}</option>
                    <option value="UPDATE">{tAdmin('logs.updated')}</option>
                    <option value="ACTIVATE">{tAdmin('logs.activated')}</option>
                    <option value="DEACTIVATE">{tAdmin('logs.deactivated')}</option>
                  </select>
                  <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Who</p>
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearchQuery !== null && userSearchQuery !== undefined ? userSearchQuery : (logsFilters.user ? getSelectedUserName() : '')}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        if (!e.target.value) {
                          setLogsFilters({...logsFilters, user: ''});
                        }
                        setUserDropdownOpen(true);
                      }}
                      onFocus={() => {
                        setUserDropdownOpen(true);
                        // Clear the input to show search query when focused
                        if (logsFilters.user && !userSearchQuery) {
                          setUserSearchQuery('');
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setUserDropdownOpen(false);
                          // Reset search query if user is selected
                          if (logsFilters.user) {
                            setUserSearchQuery('');
                          }
                        }, 200);
                      }}
                      disabled={loadingUsers}
                      className="h-10 rounded-lg border border-slate-200 bg-white px-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder={tAdmin('logs.searchByNameOrEmail')}
                    />
                    <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-400 text-[20px] pointer-events-none">arrow_drop_down</span>
                  </div>
                  
                  {/* Custom Dropdown */}
                  <AnimatePresence>
                    {userDropdownOpen && !loadingUsers && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-auto"
                      >
                        {filteredUsers.length > 0 ? (
                          <div className="p-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1 mb-1">
                              {tAdmin('users.title')}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setUserSearchQuery('');
                                setLogsFilters({...logsFilters, user: ''});
                                setUserDropdownOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-2"
                            >
                              <span className="material-symbols-outlined text-[18px] text-slate-400">person_off</span>
                              <span>{tAdmin('logs.allUsers')}</span>
                            </button>
                            {filteredUsers.map((user) => (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => {
                                  setUserSearchQuery('');
                                  setLogsFilters({...logsFilters, user: user.id.toString()});
                                  setUserDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-2"
                              >
                                <span className="material-symbols-outlined text-[18px] text-slate-400">person</span>
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {user.first_name} {user.last_name}
                                  </span>
                                  <span className="text-xs text-slate-500">{user.email}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-3 text-center text-sm text-slate-500">
                            {userSearchQuery ? tAdmin('logs.noMatchingUsers') : tAdmin('logs.noUsers')}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <button
                onClick={handleClearFilters}
                className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
              >
                {tAdmin('logs.clearAllFilters')}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 gap-6 items-start">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">{tAdmin('logs.operationHistory')}</h3>
                <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                  {tAdmin('logs.entry', { count: logsTotalCount })}
                </span>
              </div>
              <div className="overflow-x-auto">
                {logsError ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-red-500">{logsError}</div>
                  </div>
                ) : operationLogs.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-slate-500">{tAdmin('logs.noOperationLogs')}</div>
                  </div>
                ) : (
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                      <tr>
                        <th className="px-6 py-3 font-semibold">{tAdmin('logs.when')}</th>
                        <th className="px-6 py-3 font-semibold">Who</th>
                        <th className="px-6 py-3 font-semibold">{tAdmin('logs.what')}</th>
                        <th className="px-6 py-3 font-semibold">{tAdmin('logs.operation')}</th>
                        <th className="px-6 py-3 font-semibold">{tCommon('details')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {operationLogs.map((log, index) => {
                        const userName = formatUserName(log);
                        const actionTypeInfo = formatActionType(log.action_type);
                        return (
                          <motion.tr
                            key={log.id || index}
                            onClick={() => handleOpenLogDetails(log)}
                            className="hover:bg-slate-50 transition-colors group cursor-pointer"
                          >
                            <td className="px-6 py-4 text-slate-500 font-mono text-xs">
                              {formatDateTime(log.modified_at)}
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-semibold text-slate-900">{userName}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${getActionTypeBadgeClass(log.action_type)}`}>
                                <span className="material-symbols-outlined text-[14px]">
                                  {getActionTypeIcon(log.action_type)}
                                </span>
                                {actionTypeInfo.label}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-slate-900">{log.operation_production_line_lp || log.operation?.production_line_lp}</p>
                                {log.operation_activity && (
                                  <p className="text-xs text-slate-500">{log.operation_activity}</p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-slate-500 text-sm block max-w-md truncate" title={log.change_message || tAdmin('logs.noDetails')}>
                                {log.change_message || tAdmin('logs.noDetails')}
                              </span>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {/* Pagination */}
              {logsTotalCount > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500">
                      Showing {logsTotalCount > 0 ? startIndex : 0}-{endIndex} of {logsTotalCount}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Per page:</span>
                      <div className="relative">
                        <select
                          value={logsPageSize}
                          onChange={(e) => {
                            setLogsPageSize(Number(e.target.value));
                            setLogsPage(1);
                          }}
                          className="appearance-none rounded border border-slate-200 bg-white px-3 py-1.5 pr-8 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={logsPage === 1}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={tCommon('firstPage')}
                    >
                      <span className="material-symbols-outlined text-sm">first_page</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.max(1, logsPage - 1))}
                      disabled={logsPage === 1}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={tCommon('previousPage')}
                    >
                      <span className="material-symbols-outlined text-sm">chevron_left</span>
                    </button>
                    <span className="text-sm text-slate-700 font-medium px-3">
                      Page {logsPage} of {totalPages || 1}
                    </span>
                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, logsPage + 1))}
                      disabled={logsPage >= totalPages}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={tCommon('nextPage')}
                    >
                      <span className="material-symbols-outlined text-sm">chevron_right</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={logsPage >= totalPages}
                      className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={tCommon('lastPage')}
                    >
                      <span className="material-symbols-outlined text-sm">last_page</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Dashboard view (simplified)
  const renderDashboard = () => {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-main tracking-tight mb-1">Manager Dashboard</h1>
            <p className="text-text-secondary text-sm">Welcome to your operations management dashboard</p>
          </div>
        </div>
        <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
          <p className="text-text-secondary">Use the sidebar to navigate to Operations or Operation Logs.</p>
        </div>
      </div>
    );
  };


  // Prepare user data for sidebar
  const userName = currentUser 
    ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email || t('managerSubtitleDefault')
    : t('managerSubtitleDefault');
  const userSubtitle = t('managerSubtitleDefault');
  const userProfileImage = currentUser?.profile_picture 
    ? getProfilePictureUrl(currentUser.profile_picture)
    : null;

  // Get page title based on active menu item
  const getPageTitle = (menuItem) => {
    return t(`pageTitles.${menuItem}`, { defaultValue: t('pageTitles.dashboard') });
  };

  return (
    <div className="bg-background text-text-main overflow-hidden h-screen flex">
      <AdminSidebar
        activeMenuItem={activeMenuItem}
        onMenuItemClick={handleMenuItemClick}
        onSignOut={handleSignOut}
        onProfileClick={handleProfileClick}
        userName={userName}
        userSubtitle={userSubtitle}
        userProfileImage={userProfileImage}
        userInitials={currentUser?.initials || ''}
        alertCount={0}
        userRole={currentUser?.role || 'MANAGER'}
        isMobileOpen={isMobileMenuOpen}
        onClose={handleCloseMobileMenu}
      />
      <main className="flex-1 min-w-0 flex flex-col h-full overflow-hidden bg-background relative">
        <AdminTopBar
          title={getPageTitle(activeMenuItem)}
          searchPlaceholder={tManager('dashboardChrome.searchLogs')}
          onSearch={handleSearch}
          onMenuClick={handleMenuClick}
          onHelpClick={handleHelpClick}
          onSettingsClick={handleSettingsClick}
          isNotificationOpen={isNotificationOpen}
          onNotificationToggle={() => setIsNotificationOpen(prev => !prev)}
          notificationUnreadCount={notificationUnreadCount}
          leftExtra={
            activeMenuItem === 'teams-planning' ? (
              <TeamsPlanningFilters
                datePreset={teamsPlanningFilters.datePreset}
                startDate={teamsPlanningFilters.startDate}
                endDate={teamsPlanningFilters.endDate}
                onDatePresetChange={teamsPlanningFilters.setDatePreset}
                onStartChange={teamsPlanningFilters.setStartDate}
                onEndChange={teamsPlanningFilters.setEndDate}
                pretendedHours={teamsPlanningFilters.pretendedHours}
                onPretendedHoursChange={teamsPlanningFilters.setPretendedHours}
                workingDays={teamsPlanningFilters.workingDays}
                onWorkingDaysChange={teamsPlanningFilters.setWorkingDays}
              />
            ) : null
          }
          actions={
            activeMenuItem === 'teams-planning' ? (
              <div className="teams-planning-page flex items-center gap-2">
                <TeamsPlanningSeeDetailsButton
                  members={teamsPlanningFilters.filterOptions.members || []}
                  centre={teamsPlanningFilters.centre}
                  team={teamsPlanningFilters.team}
                  initialMemberId={teamsPlanningFilters.member}
                />
                <TeamsPlanningExportButton filters={teamsPlanningFilters.apiFilters} />
              </div>
            ) : null
          }
          beforeNotifications={
            activeMenuItem === 'teams-planning' ? (
              <TeamsPlanningPeopleFiltersToggle
                expanded={teamsPlanningFilters.peopleFiltersExpanded}
                onToggle={teamsPlanningFilters.togglePeopleFiltersExpanded}
              />
            ) : null
          }
        />
        {activeMenuItem === 'teams-planning' ? (
          <TeamsPlanningPeopleFiltersPanel expanded={teamsPlanningFilters.peopleFiltersExpanded}>
            <TeamsPlanningCentreFilterRow
              centre={teamsPlanningFilters.centre}
              onCentreChange={teamsPlanningFilters.setCentre}
              centres={teamsPlanningFilters.filterOptions.centres || []}
            />
            <TeamsPlanningTeamFilterRow
              team={teamsPlanningFilters.team}
              onTeamChange={teamsPlanningFilters.setTeam}
              teams={teamsPlanningFilters.filterOptions.teams || []}
              centre={teamsPlanningFilters.centre}
            />
            <TeamsPlanningMemberFilterRow
              member={teamsPlanningFilters.member}
              onMemberChange={teamsPlanningFilters.setMember}
              members={teamsPlanningFilters.filterOptions.members || []}
              centre={teamsPlanningFilters.centre}
              team={teamsPlanningFilters.team}
            />
          </TeamsPlanningPeopleFiltersPanel>
        ) : null}
        <div className="flex-1 overflow-y-auto p-4 scroll-smooth">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeMenuItem}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              transition={{ 
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="container mx-auto flex flex-col gap-8"
            >
            {activeMenuItem === 'dashboard' ? (
              <ManagerDashboardView />
            ) : activeMenuItem === 'imputations' ? (
              <ImputationsTablePage />
            ) : activeMenuItem === 'my-teams' ? (
              <ManagerTeamsPage />
            ) : activeMenuItem === 'teams-planning' ? (
              <TeamsPlanningPage
                filters={teamsPlanningFilters.apiFilters}
                onFilterOptionsChange={teamsPlanningFilters.setFilterOptions}
              />
            ) : activeMenuItem === 'operations' ? (
              <OperationsPage onNavigate={setActiveMenuItem} />
            ) : activeMenuItem === 'badge-configuration' ? (
              <BadgeConfigurationPage />
            ) : activeMenuItem === 'reward-management' ? (
              <RewardManagementPage />
            ) : activeMenuItem === 'performance-tracking' ? (
              <GlobalPerformanceTrackingPage />
            ) : activeMenuItem === 'operation-logs' ? (
              renderOperationLogsPage()
            ) : activeMenuItem === 'badge-disputes' ? (
              <BadgeDisputeManagementPage />
            ) : (
              renderDashboard()
            )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <NotificationSidebar
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        onUnreadCountChange={setNotificationUnreadCount}
      />

      {/* Change Details Modal */}
      <AnimatePresence>
        {showLogDetailsModal && selectedLogItem && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleCloseLogDetails}
            />
            {/* Modal */}
            <div className="fixed inset-0 z-[51] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-4xl w-full border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="p-6 border-b border-slate-200 bg-slate-50/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-text-main mb-1">
                        {tAdmin('logs.changeDetails')}
                      </h3>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-sm text-text-secondary">
                          <span className="font-medium">{selectedLogItem.operation_production_line_lp || selectedLogItem.operation?.production_line_lp}</span>
                          {selectedLogItem.operation_activity && (
                            <span> - {selectedLogItem.operation_activity}</span>
                          )}
                        </span>
                        {selectedLogItem.action_type && (
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold border ${formatActionType(selectedLogItem.action_type).color}`}>
                            {formatActionType(selectedLogItem.action_type).label}
                          </span>
                        )}
                      </div>
                      {selectedLogItem.change_message && (
                        <p className="text-sm text-text-secondary mt-2 italic">
                          {selectedLogItem.change_message}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleCloseLogDetails}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[24px]">close</span>
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {selectedLogItem.action_type === 'CREATE' ? (
                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-text-main uppercase tracking-wide">{tAdmin('logs.newValues')}</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-text-secondary mb-1">{tAdmin('logs.code')}</p>
                          <p className="text-sm text-text-main font-medium">{formatFieldValue(selectedLogItem.new_code, tAdmin('logs.code'))}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary mb-1">{tAdmin('logs.name')}</p>
                          <p className="text-sm text-text-main font-medium">{formatFieldValue(selectedLogItem.new_name, tAdmin('logs.name'))}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary mb-1">{tAdmin('logs.domain')}</p>
                          <p className="text-sm text-text-main">{formatFieldValue(selectedLogItem.new_domain, tAdmin('logs.domain'))}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary mb-1">{tAdmin('logs.standardTime')}</p>
                          <p className="text-sm text-text-main">{formatFieldValue(selectedLogItem.new_temps_standard, tAdmin('logs.standardTime'))}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-text-secondary mb-1">{tAdmin('logs.description')}</p>
                          <p className="text-sm text-text-main whitespace-pre-wrap">{formatFieldValue(selectedLogItem.new_description, tAdmin('logs.description'))}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary mb-1">{tAdmin('logs.activeStatus')}</p>
                          <p className="text-sm text-text-main">{formatFieldValue(selectedLogItem.new_is_active, tAdmin('logs.activeStatus'))}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-text-main uppercase tracking-wide">{tAdmin('logs.fieldChanges')}</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">{tAdmin('logs.field')}</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">{tAdmin('logs.oldValue')}</th>
                              <th className="px-4 py-3 w-8"></th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">{tAdmin('logs.newValue')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {getChangedFields(selectedLogItem).map((field, index) => (
                              <tr key={index} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-medium text-text-main">{field.name}</td>
                                <td className="px-4 py-3 text-text-secondary">
                                  <span className={`${field.old ? '' : 'text-slate-400'}`}>
                                    {formatFieldValue(field.old, field.name)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="material-symbols-outlined text-primary text-[18px]">arrow_forward</span>
                                </td>
                                <td className="px-4 py-3 text-text-main font-medium">
                                  {formatFieldValue(field.new, field.name)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {getChangedFields(selectedLogItem).length === 0 && (
                          <div className="text-center py-8 text-text-secondary">
                            <span className="material-symbols-outlined text-4xl mb-2">info</span>
                            <p>{tAdmin('logs.noFieldChanges')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-slate-200 bg-slate-50/50 flex justify-end">
                  <button
                    onClick={handleCloseLogDetails}
                    className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-100 rounded-lg transition-colors border border-slate-300"
                  >
                    {tCommon('close')}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManagerDashboard;
